import type { TechlineCaseSummary } from '../entities';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { buildCaseSummariesRequest, toTechlineCaseSummary } from '../helpers';
import { type TechlineStatusCode } from '../types';

/** Parameters for fetching a filtered list of Techline cases. */
export type FetchCasesParams = {
    /** Optional case number filter. */
    caseNumber?: string;

    /** Optional case owner filter. */
    caseOwner?: string;

    /** Optional case type filter. */
    caseType?: string;

    /** Optional flag indicating the request originates from a mobile client. */
    isMobile?: boolean;

    /** Optional page number for paginated results. */
    page?: string;

    /** Optional page size for paginated results. */
    recordCount?: string;

    /** The retailer number associated with the cases. */
    retailerNumber: string;

    /** Optional repair order number filter. */
    roNumber?: string;

    /** Optional status code filter. */
    statusCode?: string;

    /** Optional submission date filter. */
    submissionDate?: string;

    /** Optional Techline case status filter. */
    techlineCaseStatus?: TechlineStatusCode[];

    /** Optional VIN filter. */
    vin?: string;

    /** Optional worksheets filter. */
    worksheets?: string;
};

/** The result of fetching a list of Techline cases. */
export type FetchCasesResult = {
    /** The ending number of the current page of results. */
    endCount: number;

    /** The list of cases matching the filter criteria. */
    results: TechlineCaseSummary[];

    /** The starting number of the current page of results. */
    startCount: number;

    /** The total number of records matching the filter criteria. */
    totalRecords: string;
};

/**
 * Fetch a filtered, paginated list of Techline cases from NorthStar.
 * Returns the matching case summaries and total record count.
 */
export class FetchCases extends StsBackendOperation {
    /**
     * Fetch cases matching the given filter criteria.
     *
     * @param params - the filter and pagination parameters
     * @param signal - optional abort signal for request cancellation
     * @returns the matching cases and total count, or undefined if the API call failed
     */
    async exec(params: FetchCasesParams, signal?: AbortSignal): Promise<FetchCasesResult | undefined> {
        const response = await this.client.getCases(buildCaseSummariesRequest(params), signal);

        if (!response.success || !response.data) {
            return undefined;
        }

        return {
            endCount: response.data.endCount,
            results: response.data.results.map(toTechlineCaseSummary),
            startCount: response.data.startCount,
            totalRecords: response.data.totalRecords,
        };
    }
}
